import { useState, useEffect } from 'react';
import { Bell, Calendar, AlertCircle, CheckCircle, Clock, User, Users, Mail, Phone, MapPin, Briefcase, RefreshCw, Info, MessageCircle } from 'lucide-react';
import api from '../api';
import WhatsAppModal from '../components/WhatsAppModal';  // Task 22

export default function FollowUps() {
  const [followUps, setFollowUps] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, due_today: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [includeSubordinates, setIncludeSubordinates] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState(null);
  
  // Task 22: WhatsApp modal state
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Get user info from localStorage
  useEffect(() => {
    const userStr = localStorage.getItem('spes_user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  // Fetch follow-ups from API
  const fetchFollowUps = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('spes_token');
      
      const params = new URLSearchParams();
      params.append('status', statusFilter);
      params.append('include_subordinates', includeSubordinates);
      
      // Use the same endpoint as MyFollowUps for consistency
      const response = await api.get('/followups/my', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Combine all follow-up categories for table view
      const allFollowUps = [
        ...response.data.overdue || [],
        ...response.data.today || [],
        ...response.data.upcoming || [],
        ...response.data.completed || []
      ];
      
      // Map FollowUp data structure to match table expectations
      const mappedFollowUps = allFollowUps.map(followUp => {
        const today = new Date();
        const targetDate = new Date(followUp.target_date);
        const daysUntil = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
        
        return {
          ...followUp,
          follow_up_datetime: followUp.target_date, // Map target_date to follow_up_datetime
          status: followUp.status,
          candidate_phone: '', // Not available in FollowUp model
          candidate_email: '', // Not available in FollowUp model
          last_call_outcome: '', // Not available in FollowUp model
          next_action: followUp.notes || 'No action specified',
          days_until: daysUntil // Add calculated days until
        };
      });
      
      // Filter by status if needed
      let filteredFollowUps = mappedFollowUps;
      if (statusFilter !== 'all') {
        const statusMap = {
          'overdue': 'overdue',
          'today': 'today', 
          'pending': 'upcoming',
          'completed': 'completed'
        };
        const category = statusMap[statusFilter] || statusFilter;
        filteredFollowUps = mappedFollowUps.filter(f => {
          const today = new Date();
          const targetDate = new Date(f.target_date);
          if (category === 'overdue') return targetDate < today && f.status === 'pending';
          if (category === 'today') return targetDate.toDateString() === today.toDateString();
          if (category === 'upcoming') return targetDate > today && f.status === 'pending';
          if (category === 'completed') return f.status === 'completed';
          return true;
        });
      }
      
      setFollowUps(filteredFollowUps);
      
      // Calculate summary from response
      setSummary({
        total: allFollowUps.length,
        pending: (response.data.upcoming || []).length,
        due_today: (response.data.today || []).length,
        overdue: (response.data.overdue || []).length
      });
    } catch (err) {
      console.error('Error fetching follow-ups:', err);
      setError(err.response?.data?.detail || 'Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchFollowUps();
  }, [statusFilter, includeSubordinates]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchFollowUps();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [statusFilter, includeSubordinates]);

  // Filter by search term
  const filteredFollowUps = (followUps || []).filter(followUp => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      followUp.candidate_name?.toLowerCase().includes(term) ||
      followUp.candidate_email?.toLowerCase().includes(term) ||
      followUp.candidate_phone?.toLowerCase().includes(term)
    );
  });

  // Format date and time
  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Task 22: Handle WhatsApp for follow-up candidates
  const handleWhatsApp = (followUp) => {
    const candidate = {
      id: followUp.candidate_id,
      name: followUp.candidate_name,
      email: followUp.candidate_email,
      phone: followUp.candidate_phone,
      location: followUp.candidate_location
    };
    setSelectedCandidate(candidate);
    setWhatsappModalOpen(true);
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const styles = {
      overdue: 'bg-red-100 text-red-800 border-red-300',
      due_today: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      pending: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    
    const labels = {
      overdue: 'Overdue',
      due_today: 'Due Today',
      pending: 'Pending'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  // Get days until due badge
  const getDaysUntilDueBadge = (days) => {
    if (days < 0) {
      return <span className="text-red-600 font-semibold">{Math.abs(days)} days overdue</span>;
    } else if (days === 0) {
      return <span className="text-yellow-600 font-semibold">Today</span>;
    } else if (days === 1) {
      return <span className="text-blue-600">Tomorrow</span>;
    } else {
      return <span className="text-blue-600">In {days} days</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold text-gold">Follow-Up Tracking</h1>
          <p className="text-textLight/70 mt-1">Manage and track scheduled follow-ups</p>
        </div>
        <button
          onClick={fetchFollowUps}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 bg-teal hover:bg-teal-light text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Summary Cards - Task 15: Enhanced with hover effects and brand colors */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div 
          className="bg-gradient-to-br from-navy-dark to-navy border border-teal/30 rounded-xl p-6 shadow-lg hover:shadow-teal/30 hover:scale-105 transition-all duration-300 cursor-pointer group"
          title="Total follow-ups across all statuses"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-textLight/70 text-sm font-body mb-1">Total Follow-Ups</p>
              <p className="text-4xl font-heading font-bold text-gold mt-2 group-hover:text-gold-light transition-colors">{summary.total}</p>
            </div>
            <Bell className="text-teal group-hover:text-teal-light transition-colors" size={36} />
          </div>
        </div>

        <div 
          className="bg-gradient-to-br from-navy-dark to-navy border border-red-500/30 rounded-xl p-6 shadow-lg hover:shadow-red-500/30 hover:scale-105 transition-all duration-300 cursor-pointer group"
          title="Follow-ups past their due date"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-textLight/70 text-sm font-body mb-1">Overdue</p>
              <p className="text-4xl font-heading font-bold text-red-400 mt-2 group-hover:text-red-300 transition-colors">{summary.overdue}</p>
            </div>
            <AlertCircle className="text-red-400 group-hover:text-red-300 transition-colors" size={36} />
          </div>
        </div>

        <div 
          className="bg-gradient-to-br from-navy-dark to-navy border border-yellow-500/30 rounded-xl p-6 shadow-lg hover:shadow-yellow-500/30 hover:scale-105 transition-all duration-300 cursor-pointer group"
          title="Follow-ups scheduled for today"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-textLight/70 text-sm font-body mb-1">Due Today</p>
              <p className="text-4xl font-heading font-bold text-[#f59e0b] mt-2 group-hover:text-yellow-300 transition-colors">{summary.due_today}</p>
            </div>
            <Calendar className="text-[#f59e0b] group-hover:text-yellow-300 transition-colors" size={36} />
          </div>
        </div>

        <div 
          className="bg-gradient-to-br from-navy-dark to-navy border border-[#14b8a6]/30 rounded-xl p-6 shadow-lg hover:shadow-[#14b8a6]/30 hover:scale-105 transition-all duration-300 cursor-pointer group"
          title="Upcoming follow-ups"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-textLight/70 text-sm font-body mb-1">Pending</p>
              <p className="text-4xl font-heading font-bold text-[#14b8a6] mt-2 group-hover:text-teal-light transition-colors">{summary.pending}</p>
            </div>
            <Clock className="text-[#14b8a6] group-hover:text-teal-light transition-colors" size={36} />
          </div>
        </div>
      </div>

      {/* Filters - Task 15: Improved spacing and layout */}
      <div className="bg-navy-dark border border-teal/30 rounded-xl p-8 shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-textLight mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 bg-navy border border-teal/30 rounded-lg text-textLight focus:ring-2 focus:ring-teal focus:border-transparent"
            >
              <option value="all">All Follow-Ups</option>
              <option value="overdue">Overdue</option>
              <option value="due_today">Due Today</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-textLight mb-2">Search Candidate</label>
            <input
              type="text"
              placeholder="Name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-navy border border-teal/30 rounded-lg text-textLight placeholder-textLight/50 focus:ring-2 focus:ring-teal focus:border-transparent"
            />
          </div>

          {/* Include Subordinates (for managers) - Task 15: Redesigned with icon and tooltip */}
          {user && ['bm', 'am', 'tl', 'admin'].includes(user.role) && (
            <div className="flex items-end">
              <div className="group relative">
                <label className="flex items-center space-x-3 cursor-pointer px-4 py-3 bg-navy/50 border border-teal/20 rounded-lg hover:border-teal/40 hover:bg-navy/70 transition-all">
                  <input
                    type="checkbox"
                    checked={includeSubordinates}
                    onChange={(e) => setIncludeSubordinates(e.target.checked)}
                    className="w-5 h-5 rounded border-teal/30 text-teal focus:ring-2 focus:ring-teal bg-navy cursor-pointer"
                  />
                  <Users className="text-teal" size={18} />
                  <span className="text-sm text-textLight font-medium">Include Subordinates</span>
                  <Info className="text-textLight/40 hover:text-teal" size={16} />
                </label>
                {/* Tooltip */}
                <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50">
                  <div className="bg-navy-dark border border-teal/40 rounded-lg px-3 py-2 shadow-xl">
                    <p className="text-xs text-textLight whitespace-nowrap">Show follow-ups of your reporting team</p>
                  </div>
                  <div className="w-3 h-3 bg-navy-dark border-r border-b border-teal/40 transform rotate-45 absolute left-6 -bottom-1.5"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Follow-Ups Table - Task 15: Improved readability with sticky header and alternating rows */}
      <div className="bg-navy-dark border border-teal/30 rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-teal" size={32} />
            <span className="ml-3 text-textLight">Loading follow-ups...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="text-red-400" size={32} />
            <span className="ml-3 text-red-400">{error}</span>
          </div>
        ) : filteredFollowUps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative">
              <div className="absolute inset-0 bg-teal/20 blur-3xl rounded-full"></div>
              <CheckCircle className="text-teal relative" size={80} />
            </div>
            <h3 className="text-2xl font-heading font-bold text-gold mt-6">
              {searchTerm ? 'No matching follow-ups' : 'All caught up!'}
            </h3>
            <p className="text-textLight/70 mt-2 text-center max-w-md">
              {searchTerm 
                ? `No follow-ups match your search "${searchTerm}"` 
                : statusFilter === 'overdue'
                ? 'Great job! No overdue follow-ups — keep it up!'
                : statusFilter === 'due_today'
                ? 'No follow-ups due today. Enjoy your day!'
                : 'No follow-ups scheduled at the moment.'}
            </p>
            {statusFilter !== 'all' && (
              <button
                onClick={() => setStatusFilter('all')}
                className="mt-6 px-6 py-2 bg-teal/20 hover:bg-teal/30 text-teal border border-teal/40 rounded-lg transition-all"
              >
                View all follow-ups
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1e3a5f] border-b border-teal/30 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Role & Location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Follow-Up Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Days Until Due</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Next Action</th>
                  {user && ['bm', 'am', 'tl', 'admin'].includes(user.role) && includeSubordinates && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-teal uppercase tracking-wider">Recruiter</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-teal/10">
                {filteredFollowUps.map((followUp, index) => {
                  const { date, time } = formatDateTime(followUp.follow_up_datetime);
                  return (
                    <tr key={followUp.id} className={`hover:bg-teal/5 transition-colors ${
                      index % 2 === 0 ? 'bg-navy/30' : 'bg-navy-dark'
                    }`}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getStatusBadge(followUp.status)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-textLight font-semibold">{followUp.candidate_name}</div>
                        {followUp.last_call_outcome && (
                          <div className="text-xs text-textLight/60 mt-1">
                            Last: {followUp.last_call_outcome}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col space-y-1 text-sm">
                          {followUp.candidate_email && (
                            <div className="flex items-center space-x-2 text-textLight/80">
                              <Mail size={14} className="text-teal/70" />
                              <span className="truncate max-w-[200px]">{followUp.candidate_email}</span>
                            </div>
                          )}
                          {followUp.candidate_phone && (
                            <div className="flex items-center space-x-2 text-textLight/80">
                              <Phone size={14} className="text-teal/70" />
                              <span>{followUp.candidate_phone}</span>
                            </div>
                          )}
                          {/* Task 22: WhatsApp Button */}
                          {followUp.candidate_phone && (
                            <div className="mt-2">
                              <button
                                onClick={() => handleWhatsApp(followUp)}
                                className="inline-flex items-center px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                title="Send WhatsApp reminder"
                              >
                                <MessageCircle size={12} className="mr-1" />
                                WhatsApp
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col space-y-1 text-sm">
                          {followUp.candidate_role && (
                            <div className="flex items-center space-x-2 text-textLight/80">
                              <Briefcase size={14} className="text-teal/70" />
                              <span>{followUp.candidate_role}</span>
                            </div>
                          )}
                          {followUp.candidate_location && (
                            <div className="flex items-center space-x-2 text-textLight/80">
                              <MapPin size={14} className="text-teal/70" />
                              <span>{followUp.candidate_location}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-textLight font-medium">{date}</div>
                        <div className="text-xs text-textLight/60">{time}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getDaysUntilDueBadge(followUp.days_until_due)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-textLight/80 max-w-[300px]">
                          {followUp.next_action || <span className="text-textLight/40 italic">No action notes</span>}
                        </div>
                      </td>
                      {user && ['bm', 'am', 'tl', 'admin'].includes(user.role) && includeSubordinates && (
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2 text-sm text-textLight/80">
                            <User size={14} className="text-teal/70" />
                            <span>{followUp.recruiter_name}</span>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Results Summary */}
      {!loading && !error && filteredFollowUps.length > 0 && (
        <div className="text-center text-sm text-textLight/60">
          Showing {filteredFollowUps.length} of {followUps.length} follow-ups
          {searchTerm && ` matching "${searchTerm}"`}
        </div>
      )}
    
    {/* Task 22: WhatsApp Modal */}
    <WhatsAppModal
      isOpen={whatsappModalOpen}
      onClose={() => {
        setWhatsappModalOpen(false);
        setSelectedCandidate(null);
      }}
      candidate={selectedCandidate}
    />
  </div>
  );
}
